import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle, Phone, Sun, Battery, Zap, Droplets, Snowflake,
  Fan, Plug, Layers, Wind, Thermometer, ClipboardCheck, Flame, Users, Home,
  FileText, Wallet, Percent, BadgeCheck, Sparkles, Leaf, HelpCircle,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const CTA = '/get-estimate?source=nsw-home-energy-saver';
const HERO_IMG = '/product-images/fox-ess/battery-hero.jpg';

// Eligible upgrades. `seh` marks the ones Supa Energy Hub supplies & installs.
const UPGRADES = [
  { icon: Sun, name: 'Rooftop solar system', seh: true },
  { icon: Battery, name: 'Residential battery', seh: true },
  { icon: Zap, name: 'Switchboard upgrade', seh: true },
  { icon: Droplets, name: 'Heat pump water heater', seh: true },
  { icon: Sun, name: 'Solar water heater', seh: true },
  { icon: Snowflake, name: 'Reverse cycle air conditioner', seh: true },
  { icon: Plug, name: 'EV level 2 charger', seh: true },
  { icon: Flame, name: 'Induction cooktop', seh: false },
  { icon: Fan, name: 'DC ceiling fan', seh: false },
  { icon: Layers, name: 'Ceiling insulation', seh: false },
  { icon: Wind, name: 'Draught-proofing', seh: false },
  { icon: Thermometer, name: 'Double glazing', seh: false },
  { icon: ClipboardCheck, name: 'NatHERS assessment', seh: false },
];

const FAQS = [
  {
    q: 'What is the NSW Home Energy Saver?',
    a: 'It is a NSW Government program that helps eligible households cover the upfront cost of energy-saving home upgrades. It offers two types of support: a discount of up to $4,000 off eligible upgrades, and a zero-interest loan of up to $15,000 repayable over up to 10 years.',
  },
  {
    q: 'Can I get both a discount and a loan?',
    a: 'Yes. If you are eligible for both, you can use them together — up to $4,000 off through the discount, and a zero-interest loan to cover any remaining cost. If you want both, it is recommended you apply for the discount first.',
  },
  {
    q: 'Do I have to pay anything myself?',
    a: 'Yes. To receive a discount you must contribute a one-off minimum payment of at least $200 towards the total cost of your upgrade (the minimum customer contribution).',
  },
  {
    q: 'Who delivers the program?',
    a: 'The program is funded by the NSW Government. Discounts are delivered by Creditex, and zero-interest loans are delivered by Brighte and Plenti. Supa Energy Hub supplies and installs many of the eligible upgrades and can provide the quotes you need for your application.',
  },
  {
    q: 'I rent or own a strata property — can I still apply?',
    a: 'Renters may be eligible for discounts (coming soon) with landlord approval, and strata properties can qualify if the owners corporation agrees to the upgrade. Landlords can apply for a Home Energy Saver loan to upgrade their property.',
  },
  {
    q: 'When can I apply?',
    a: 'Zero-interest loan applications are open now. Discount applications are opening soon — we can help you get prepared so you are ready the moment they open.',
  },
];

const NSWHomeEnergySaverPage = () => (
  <div className="min-h-screen">
    <SEO
      title="NSW Home Energy Saver — Discounts &amp; Zero-Interest Loans Explained"
      description="A simple guide to the NSW Home Energy Saver program: get a discount of up to $4,000 or a zero-interest loan up to $15,000 on solar, batteries, heat pump hot water, reverse cycle air conditioning and more. See how it works, who's eligible, and how Supa Energy Hub can help."
      jsonLd={[
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        },
      ]}
    />
    <Header />
    <main>
      {/* HERO */}
      <section className="pt-28 pb-12 sm:pb-16 relative overflow-hidden bg-gradient-to-br from-[#f6f8f3] via-white to-[#eaf2dc]">
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #3D5A47 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="absolute top-20 right-0 w-72 h-72 bg-[#B5D43B]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 bg-[#B5D43B]/15 border border-[#B5D43B]/40 rounded-full px-3 py-1 mb-4">
                <Sparkles className="w-3.5 h-3.5 text-[#3D5A47]" />
                <span className="text-[11px] font-bold tracking-wider uppercase text-[#3D5A47]">New South Wales · Government Program</span>
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#3D5A47] leading-[1.1] tracking-tight">
                The NSW <span className="text-[#B5D43B] bg-[#3D5A47] px-2 rounded-md">Home Energy Saver</span>, made simple.
              </h1>
              <p className="text-base sm:text-lg text-gray-700 mt-5 max-w-2xl leading-relaxed">
                Make your home cheaper to run and more comfortable to live in. Through the NSW Government&rsquo;s
                Home Energy Saver, eligible households can get a <strong>discount of up to $4,000</strong> or a
                <strong> zero-interest loan of up to $15,000</strong> on upgrades like solar, batteries and heat pump hot water.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Link to={CTA}>
                  <Button className="bg-[#3D5A47] hover:bg-[#2a4032] text-white gap-2 text-sm sm:text-base font-semibold px-5 py-5 rounded-xl shadow-lg shadow-[#3D5A47]/20" data-testid="nsw-hero-cta">
                    See What I Could Install <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:0478453548">
                  <Button variant="outline" className="bg-white border-[#3D5A47]/20 text-[#3D5A47] hover:bg-[#f8faf5] gap-2 text-sm sm:text-base px-5 py-5 rounded-xl" data-testid="nsw-hero-call">
                    <Phone className="w-4 h-4" /> Talk to Us — 0478 453 548
                  </Button>
                </a>
              </div>
              <p className="text-[11px] text-gray-500 mt-5">
                Information sourced from the{' '}
                <a href="https://www.energy.nsw.gov.au/households/grants-rebates/home-energy-saver" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#3D5A47]">
                  NSW Climate and Energy Action · Home Energy Saver
                </a>{' '}program page. Always confirm your eligibility with the official program.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-3xl shadow-xl overflow-hidden border border-[#B5D43B]/30">
                <img src={HERO_IMG} alt="A NSW family outside their home with rooftop solar" className="w-full h-auto object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TWO TYPES OF SUPPORT */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-3">Two ways to save</span>
            <h2 className="heading-2 text-[#3D5A47]">Choose a discount, a loan — or both.</h2>
            <p className="body-large text-gray-600 mt-4">
              Energy-saving upgrades lower your bills and make your home more comfortable, but the upfront cost can add up.
              Home Energy Saver helps cover that cost so the upgrade is within reach.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {/* DISCOUNT */}
            <div className="bg-[#f8faf5] rounded-2xl border-2 border-[#B5D43B]/40 p-8 relative" data-testid="nsw-discount-card">
              <span className="absolute top-0 left-8 -translate-y-1/2 bg-[#B5D43B] text-[#1a2820] text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                Coming soon
              </span>
              <div className="w-12 h-12 rounded-xl bg-[#3D5A47] flex items-center justify-center mb-4">
                <Percent className="w-6 h-6 text-[#B5D43B]" />
              </div>
              <p className="text-5xl font-black text-[#3D5A47] mb-1">Up to $4,000</p>
              <h3 className="font-bold text-[#3D5A47] mb-3">Discount off eligible upgrades</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span>For homeowners <strong>and renters</strong></span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span>Combined household income up to <strong>$80,000</strong>, <em>or</em> an eligible concession card</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span>One-off minimum contribution from <strong>$200</strong></span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span>Delivered by Creditex · applications opening soon</span></li>
              </ul>
            </div>
            {/* LOAN */}
            <div className="bg-[#f8faf5] rounded-2xl border-2 border-[#3D5A47] p-8 relative" data-testid="nsw-loan-card">
              <span className="absolute top-0 left-8 -translate-y-1/2 bg-[#3D5A47] text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                Open now
              </span>
              <div className="w-12 h-12 rounded-xl bg-[#3D5A47] flex items-center justify-center mb-4">
                <Wallet className="w-6 h-6 text-[#B5D43B]" />
              </div>
              <p className="text-5xl font-black text-[#3D5A47] mb-1">Up to $15,000</p>
              <h3 className="font-bold text-[#3D5A47] mb-3">Zero-interest loan</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span>For homeowners <strong>and landlords</strong></span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span>Combined household income up to <strong>$210,000</strong></span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span><strong>0% interest</strong>, repayable over up to 10 years</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span>Delivered by Brighte &amp; Plenti · applications open now</span></li>
              </ul>
            </div>
          </div>
          <div className="mt-6 flex items-start gap-3 bg-[#f0f7e6] border border-[#B5D43B]/40 rounded-xl p-4 max-w-3xl">
            <BadgeCheck className="w-5 h-5 text-[#3D5A47] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700">
              Eligible for both? You can combine them — take up to $4,000 off with the discount, then use a zero-interest loan
              for the rest. If you want both, it&rsquo;s recommended you apply for the discount first.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT CAN BE FUNDED */}
      <section className="py-20 bg-[#f8faf5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-3">What you can upgrade</span>
            <h2 className="heading-2 text-[#3D5A47]">13 eligible energy-saving upgrades</h2>
            <p className="body-large text-gray-600 mt-4">
              Use your discount or loan on any of the upgrades below. Items marked <span className="inline-flex items-center gap-1 text-[#3D5A47] font-semibold"><CheckCircle className="w-4 h-4 text-[#B5D43B]" />We install this</span> are ones Supa Energy Hub can supply and fit for you.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="nsw-upgrades-grid">
            {UPGRADES.map((u) => (
              <div key={u.name} className={`bg-white rounded-xl p-5 border ${u.seh ? 'border-[#B5D43B]/50' : 'border-gray-100'} flex flex-col`} data-testid={`nsw-upgrade-${u.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="w-10 h-10 rounded-lg bg-[#B5D43B]/15 flex items-center justify-center mb-3">
                  <u.icon className="w-5 h-5 text-[#3D5A47]" />
                </div>
                <h3 className="font-bold text-sm text-[#3D5A47] leading-snug">{u.name}</h3>
                {u.seh && (
                  <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#3D5A47]">
                    <CheckCircle className="w-3.5 h-3.5 text-[#B5D43B]" /> We install this
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IS ELIGIBLE */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-3">Who can apply</span>
            <h2 className="heading-2 text-[#3D5A47]">Am I eligible?</h2>
            <p className="body-large text-gray-600 mt-4">
              Eligibility depends on whether you&rsquo;re after the discount or the loan. Here&rsquo;s the simple version.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#f8faf5] rounded-2xl border border-gray-100 p-7" data-testid="nsw-eligible-loan">
              <div className="flex items-center gap-2 mb-4 text-[#3D5A47]"><Wallet className="w-5 h-5" /><span className="text-sm font-bold uppercase tracking-wide">For the zero-interest loan</span></div>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span>You <strong>own the property</strong> (homeowners and landlords)</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span>Australian citizen or permanent resident</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span>Combined annual taxable household income up to <strong>$210,000</strong></span></li>
              </ul>
            </div>
            <div className="bg-[#f8faf5] rounded-2xl border border-gray-100 p-7" data-testid="nsw-eligible-discount">
              <div className="flex items-center gap-2 mb-4 text-[#3D5A47]"><Percent className="w-5 h-5" /><span className="text-sm font-bold uppercase tracking-wide">For the discount (coming soon)</span></div>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span>You <strong>own</strong> the property, or are a <strong>tenant named on the lease</strong></span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span>Combined annual household income up to <strong>$80,000</strong>, <em>or</em></span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" /><span>You hold a <strong>Health Care Card</strong>, <strong>Low Income Health Care Card</strong>, <strong>Pensioner Concession Card</strong> or <strong>Veteran Gold Card</strong></span></li>
              </ul>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-6 max-w-3xl">
            Final eligibility, exclusions and product requirements are set and confirmed by the NSW Government program and its
            delivery partners. The figures above are a guide only — always check the official guidelines for your situation.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS / STEPS */}
      <section className="py-20 bg-[#f8faf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-3">How it works</span>
            <h2 className="heading-2 text-[#3D5A47]">From idea to installed — in a few simple steps</h2>
          </div>
          <ol className="relative border-l-2 border-[#B5D43B]/30 pl-8 space-y-10">
            {[
              { icon: HelpCircle, title: 'Check your eligibility', detail: 'Confirm whether you qualify for the discount, the loan, or both, using the official NSW guidelines and tools — or just ask us and we will point you in the right direction.' },
              { icon: Sun, title: 'Choose your upgrade', detail: 'Decide which energy-saving upgrade is right for your home — solar, a battery, heat pump hot water, reverse cycle air conditioning, an EV charger, and more.' },
              { icon: FileText, title: 'Get a quote from an approved supplier', detail: 'Approved suppliers provide the quotes used in your application. Supa Energy Hub can quote and install the upgrades we offer, so this step is easy.' },
              { icon: Wallet, title: 'Apply through a finance provider', detail: 'Zero-interest loans are delivered by Brighte and Plenti; discounts (coming soon) are delivered by Creditex. You finalise your application with them.' },
              { icon: CheckCircle, title: 'Get installed and start saving', detail: 'Once approved, your upgrade is installed and you start enjoying lower bills and a more comfortable home.' },
            ].map((step, i) => (
              <li key={i} className="relative" data-testid={`nsw-step-${i}`}>
                <span className="absolute -left-[42px] top-0 w-9 h-9 rounded-full flex items-center justify-center border-4 border-white bg-[#B5D43B] text-[#1a2820] shadow-md">
                  <step.icon className="w-4 h-4" />
                </span>
                <span className="text-xs font-bold uppercase tracking-widest text-[#B5D43B]">Step {i + 1}</span>
                <h3 className="text-lg font-bold mt-1 text-[#3D5A47]">{step.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{step.detail}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* HOW SEH HELPS */}
      <section className="py-20 bg-[#3D5A47] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-3">How Supa Energy Hub helps</span>
            <h2 className="heading-2 text-white">We make the upgrade part easy.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Sun, t: 'The upgrades you need', d: 'Solar, batteries, heat pump hot water, reverse cycle AC and EV chargers — all eligible upgrades we supply and install.' },
              { icon: FileText, t: 'Clear, honest quotes', d: 'We provide the quotes you need for your Home Energy Saver application, with no surprises.' },
              { icon: BadgeCheck, t: 'Accredited installers', d: 'CEC-accredited installers and quality products, so your upgrade meets program standards.' },
              { icon: Leaf, t: 'Stack your savings', d: 'Combine Home Energy Saver support with federal STCs and other incentives where eligible.' },
            ].map((c, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10" data-testid={`nsw-help-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-[#B5D43B]/20 flex items-center justify-center mb-4"><c.icon className="w-5 h-5 text-[#B5D43B]" /></div>
                <h3 className="font-bold mb-2">{c.t}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-3">FAQ</span>
          <h2 className="heading-2 text-[#3D5A47] mb-10">Your questions, answered simply</h2>
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <details key={i} className="group bg-[#f8faf5] rounded-xl border border-gray-100 overflow-hidden" data-testid={`nsw-faq-${i}`}>
                <summary className="cursor-pointer p-5 font-semibold text-[#3D5A47] flex items-center justify-between gap-4 list-none">
                  <span>{f.q}</span>
                  <span className="text-[#B5D43B] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="px-5 pb-5 text-sm text-gray-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-10">
            All figures, dates and eligibility criteria are sourced from the official{' '}
            <a href="https://www.energy.nsw.gov.au/households/grants-rebates/home-energy-saver" target="_blank" rel="noopener noreferrer" className="text-[#3D5A47] underline hover:text-[#B5D43B]">
              NSW Home Energy Saver
            </a>{' '}program page. The program is funded by the NSW Government and delivered by Creditex (discounts) and Brighte &amp; Plenti (loans). Always confirm your individual eligibility with the official program.
          </p>
        </div>
      </section>

      {/* OFFICIAL LINKS */}
      <section className="py-16 bg-[#f8faf5] border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-8">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-3">Official program links</span>
            <h2 className="heading-2 text-[#3D5A47]">Go straight to the source</h2>
            <p className="body-large text-gray-600 mt-4">
              The Home Energy Saver is funded by the NSW Government and delivered by approved partners. Use the official
              links below to confirm your eligibility and apply.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="nsw-official-links">
            {[
              { name: 'NSW Home Energy Saver', role: 'Official NSW Government program page', href: 'https://www.energy.nsw.gov.au/households/grants-rebates/home-energy-saver', tag: 'Government' },
              { name: 'Creditex', role: 'Delivers the discount (opening soon)', href: 'https://www.creditex.com.au/', tag: 'Discount' },
              { name: 'Brighte', role: 'Delivers the zero-interest loan', href: 'https://www.brighte.com.au/', tag: 'Loan' },
              { name: 'Plenti', role: 'Delivers the zero-interest loan', href: 'https://www.plenti.com.au/', tag: 'Loan' },
            ].map((l) => (
              <a
                key={l.name}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-2xl border border-gray-100 p-6 hover:border-[#B5D43B]/60 hover:shadow-md transition-all flex flex-col"
                data-testid={`nsw-official-link-${l.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <span className="self-start text-[10px] font-bold uppercase tracking-widest text-[#3D5A47] bg-[#B5D43B]/15 rounded-full px-2.5 py-1 mb-3">{l.tag}</span>
                <h3 className="font-bold text-[#3D5A47] flex items-center gap-1.5">{l.name} <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" /></h3>
                <p className="text-sm text-gray-600 mt-1 leading-relaxed">{l.role}</p>
              </a>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-6 max-w-3xl">
            Supa Energy Hub is an independent supplier and installer and is not the NSW Government or a program delivery
            partner. Always confirm your eligibility, current dates and how to apply directly with the official program and
            its delivery partners.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-br from-[#B5D43B] to-[#a3c235]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-black text-[#1a2820] mb-4">
            Ready to make your home cheaper to run?
          </h2>
          <p className="text-lg text-[#1a2820]/80 mb-8 max-w-2xl mx-auto">
            Tell us a little about your home and we&rsquo;ll show you which Home Energy Saver upgrades make sense for you —
            free, friendly and no obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={CTA}>
              <Button className="bg-[#1a2820] hover:bg-[#3D5A47] text-white gap-2 text-base font-semibold px-7 py-6 rounded-xl shadow-xl" data-testid="nsw-final-cta">
                Get My Free Estimate <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0478453548">
              <Button variant="outline" className="bg-white/40 border-[#1a2820]/30 text-[#1a2820] hover:bg-white gap-2 text-base px-7 py-6 rounded-xl" data-testid="nsw-final-call">
                <Phone className="w-4 h-4" /> Call 0478 453 548
              </Button>
            </a>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default NSWHomeEnergySaverPage;
